import React, { useState } from "react";
import { Button, Skeleton, useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { MEETING_APPS, type MeetingApp, type MeetingAppId } from "../meetingApps";
import { useMeetingApps } from "../hooks/useMeetingApps";
import MeetingAppMark from "./MeetingAppMark";

/**
 * The meeting apps this practice can hand its online appointments to, and
 * whether each one is on — My brand → Configuration, under the dish search
 * setting.
 *
 * Under My brand rather than Settings for the same reason the setting above
 * it is: it is a choice about how this practice works, not about the account.
 *
 * One row per app, and every app gets a row whether or not the backend knows
 * it yet — see `MEETING_APPS`. The rows come from that list rather than from
 * the server's `providers`, because the list is also a statement of what is
 * on the way, and an app that appeared from nowhere in a later release would
 * read as one nobody was told about. What the server says is merged onto the
 * row it names, by slug.
 *
 * Connect does one of two things, and which one is not this card's to decide
 * — see `useMeetingApps`. With no account on file for that app it hands back
 * a sign-in URL and the browser leaves; with one already linked it flips the
 * setting where it stands. Disconnect never signs anything out: it puts the
 * workspace back to making links by hand, and the account stays linked, so
 * turning it on again costs one press rather than another trip out.
 *
 * Only one app makes the links, because `meeting_provider` is one field on
 * the workspace. So connecting the second app is also what disconnects the
 * first, in one press and with both accounts kept — which the toast says, so
 * that the other row going quiet is something somebody was told about rather
 * than something they notice afterwards.
 */

/** One app's row. Split out so the states below read as one shape. */
const AppRow: React.FC<{
  app: MeetingApp;
  /** Whether this app is the one making the links. */
  enabled: boolean;
  /** Whose account is on file, where the backend named one. */
  accountEmail: string | null;
  /** Set while this row's own press is with the server. */
  busy: boolean;
  /** Set while another row's press is, which holds this one. */
  held: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}> = ({ app, enabled, accountEmail, busy, held, onConnect, onDisconnect }) => (
  <li className="flex items-center justify-between gap-4">
    <div className="flex min-w-0 items-start gap-3">
      <MeetingAppMark app={app.id} size={32} className="mt-0.5" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] 3xl:text-[16px] font-medium text-ink">
            {app.name}
          </span>
          {/* Beside the name rather than where the button is: what is being
              said is which app this is — a connected one — and a state read
              from the far end of the row is one nobody ties back to it. */}
          {enabled && (
            <span className="flex items-center gap-1.5 text-[12px] 3xl:text-[13px] font-medium text-[#16A34A]">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
              Connected
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          {/* Whose account it is, once there is one, in place of the sales
              line: somebody who has connected it has read that line already,
              and which Google account this went to is the thing they come
              back to the card to check. */}
          {enabled && accountEmail ? accountEmail : app.description}
        </p>
      </div>
    </div>

    <div className="flex-shrink-0">
      {!app.available ? (
        // Not a button, and not a disabled one either: there is nothing to
        // press and a greyed-out Connect would read as something this
        // workspace is not allowed to do rather than something that does not
        // exist yet.
        //
        // The one blue in the card, and not the brand's teal: it is a note
        // about the app beside it rather than something of ours to press, and
        // the teal everything pressable here wears would say it was.
        <span className="text-[13px] 3xl:text-[14px] font-medium text-[#007AFF]">
          Coming soon
        </span>
      ) : enabled ? (
        // Quiet, against the filled Connect it replaces. Turning an
        // integration off is a thing somebody goes looking for, not the thing
        // the row is offering, and a second filled button on the card would
        // compete with the Connect on the row under it.
        <Button
          variant="quiet"
          size="sm"
          loading={busy}
          disabled={held}
          onClick={onDisconnect}
        >
          Disconnect
        </Button>
      ) : (
        <Button size="sm" loading={busy} disabled={held} onClick={onConnect}>
          Connect
        </Button>
      )}
    </div>
  </li>
);

/** The rows' shape while the settings are still being read. */
const AppRowSkeleton: React.FC = () => (
  <li className="flex items-center justify-between gap-4">
    <div className="flex min-w-0 flex-1 items-start gap-3">
      <Skeleton className="h-8 w-8 flex-shrink-0 rounded-md" />
      <div className="min-w-0 flex-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-2 h-3 w-full max-w-[420px]" />
      </div>
    </div>
    <Skeleton className="h-8 w-24 flex-shrink-0 rounded-md" />
  </li>
);

const IntegrateAppsCard: React.FC = () => {
  const toast = useToast();
  const { loading, isEnabled, accountEmail, connect, disconnect } = useMeetingApps();

  // Which row is with the server, so its own button spins and the other is
  // held: two providers changed at once is a race the backend would settle
  // arbitrarily, and the card would then be drawing the loser.
  const [busy, setBusy] = useState<MeetingAppId | null>(null);

  const say = (what: string, reason: unknown) => {
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );
  };

  const handleConnect = async (app: MeetingApp) => {
    // Which app this one is about to displace, read before the write: the
    // setting holds one provider, so turning this on turns that off, and
    // afterwards there is nothing left on the settings to say what it was.
    const displaced = MEETING_APPS.find(
      (other) => other.id !== app.id && isEnabled(other.id)
    );

    setBusy(app.id);
    try {
      const signInUrl = await connect(app.id);
      if (signInUrl) {
        // Off to Google. `assign` rather than `replace`, so the back button
        // still comes home if somebody changes their mind on Google's screen.
        // The spinner is left running on purpose: this page is on its way
        // out, and a button that went back to saying "Connect" while the
        // browser was already leaving would read as a press that failed.
        window.location.assign(signInUrl);
        return;
      }
      // Already linked — the setting was flipped where it stood, with no
      // trip out of the app.
      toast.success(
        `${app.name} connected`,
        displaced
          ? `New online appointments will get a ${app.name} link. ${displaced.name} has been switched off.`
          : "New online appointments will get a meeting link."
      );
      setBusy(null);
    } catch (reason) {
      say(`Could not connect ${app.name}`, reason);
      setBusy(null);
    }
  };

  const handleDisconnect = async (app: MeetingApp) => {
    setBusy(app.id);
    try {
      await disconnect(app.id);
      toast.success(
        `${app.name} disconnected`,
        "Online appointments will no longer get a meeting link."
      );
    } catch (reason) {
      say(`Could not disconnect ${app.name}`, reason);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-md border border-line bg-white px-5 py-4 3xl:px-6 3xl:py-5">
      <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
        Integrate apps
      </h2>

      {/* A list, because that is what it is: the rows are the same thing said
          about different apps, and the gap between them is wide enough that
          they need no rules to be told apart. */}
      <ul className="mt-4 flex flex-col gap-5">
        {loading
          ? MEETING_APPS.map((app) => <AppRowSkeleton key={app.id} />)
          : MEETING_APPS.map((app) => (
              <AppRow
                key={app.id}
                app={app}
                enabled={isEnabled(app.id)}
                accountEmail={accountEmail(app.id)}
                busy={busy === app.id}
                held={busy !== null && busy !== app.id}
                onConnect={() => handleConnect(app)}
                onDisconnect={() => handleDisconnect(app)}
              />
            ))}
      </ul>
    </section>
  );
};

export default IntegrateAppsCard;
